import CellPrice from '@/components/GokuDataGrid/CellPrice'
import { formatCurrency, formatNumeral } from '@/utils/function'
import { HelpCircle } from 'lucide-react'
import { ReactNode } from 'react'
import { Tooltip } from 'vnetwork-ui'

type ColorVariant =
    | 'blue'
    | 'green'
    | 'amber'
    | 'teal'
    | 'red'
    | 'purple'
    | 'indigo'
    | 'cyan'

interface StatisticCardProps {
    title: string
    subtitle?: string
    value: number | ReactNode
    recordsCount?: number
    recordsLabel?: string
    colorVariant: ColorVariant
    isPrice?: boolean
    compact?: boolean
}

const colorClasses: Record<
    ColorVariant,
    {
        bg: string
        border: string
        text: string
        dot: string
    }
> = {
    blue: {
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        text: 'text-blue-700',
        dot: 'bg-blue-500',
    },
    green: {
        bg: 'bg-green-50',
        border: 'border-green-200',
        text: 'text-green-700',
        dot: 'bg-green-500',
    },
    amber: {
        bg: 'bg-amber-50',
        border: 'border-amber-200',
        text: 'text-amber-700',
        dot: 'bg-amber-500',
    },
    teal: {
        bg: 'bg-teal-50',
        border: 'border-teal-200',
        text: 'text-teal-700',
        dot: 'bg-teal-500',
    },
    red: {
        bg: 'bg-red-50',
        border: 'border-red-200',
        text: 'text-red-700',
        dot: 'bg-red-500',
    },
    purple: {
        bg: 'bg-purple-50',
        border: 'border-purple-200',
        text: 'text-purple-700',
        dot: 'bg-purple-500',
    },
    indigo: {
        bg: 'bg-indigo-50',
        border: 'border-indigo-200',
        text: 'text-indigo-700',
        dot: 'bg-indigo-500',
    },
    cyan: {
        bg: 'bg-cyan-50',
        border: 'border-cyan-200',
        text: 'text-cyan-700',
        dot: 'bg-cyan-500',
    },
}

const StatisticCard: React.FC<StatisticCardProps> = ({
    title,
    subtitle,
    value,
    recordsCount,
    recordsLabel,
    colorVariant,
    isPrice = false,
    compact = false,
}) => {
    const colors = colorClasses[colorVariant]

    if (compact) {
        return (
            <div className="flex flex-col min-w-0 px-2 py-2">
                <div className="flex items-center gap-1">
                    <span className="text-xs text-gray-500 truncate">
                        {title}
                    </span>
                    {recordsCount !== undefined && (
                        <span className="text-xs text-gray-400">
                            ({formatNumeral(recordsCount, 0)})
                        </span>
                    )}
                    {subtitle && (
                        <Tooltip title={subtitle}>
                            <HelpCircle className="w-3 h-3 text-gray-400 cursor-help flex-shrink-0" />
                        </Tooltip>
                    )}
                </div>
                <span className="text-sm font-semibold text-gray-800 mt-2">
                    {isPrice && typeof value === 'number'
                        ? formatCurrency(value)
                        : value}
                </span>
            </div>
        )
    }

    return (
        <div className={`${colors.bg} border ${colors.border} rounded-md p-4`}>
            <div className="text-sm text-gray-600 mb-1">{title}</div>
            {subtitle && (
                <div className="text-xs text-gray-500 mb-2 italic">
                    {subtitle}
                </div>
            )}
            <div className={`text-2xl font-bold ${colors.text}`}>
                {isPrice && typeof value === 'number' ? (
                    <CellPrice price={value} />
                ) : (
                    value
                )}
            </div>
            {recordsCount !== undefined && recordsLabel && (
                <div className="text-xs text-gray-500 mt-1">
                    {formatNumeral(recordsCount, 0)} {recordsLabel}
                </div>
            )}
        </div>
    )
}

export default StatisticCard
